import { AnimatePresence, motion } from "motion/react";
import { Fragment, useCallback } from "react";
import {
  type Control,
  Controller,
  useController,
  useFieldArray,
} from "react-hook-form";

import { ControlledStringInput } from "@krea/shared/design-system/ControlledInputs/StringInput";
import { FormItem } from "@krea/shared/design-system/Form";
import Checkbox from "@krea/shared/design-system/fields/Checkbox";
import { Label } from "@krea/shared/design-system/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@krea/shared/design-system/Select";

import {
  FieldRequestFieldEnum,
  FieldRequestTypeEnum,
} from "@/features/common/enums";
import type { IFieldRequestInput } from "@/features/lender-responses/types/lender-response";
import { FIELD_REQUESTS_LABELS } from "@/utils/fieldRequests/fieldRequests";
import type { BidPostData } from "@/validations";

import DetailsItem from "../../../details/item";

const reducedGuarantorPercentToOptions = [
  { value: "100", label: "100%" },
  { value: "90", label: "90%" },
  { value: "80", label: "80%" },
  { value: "70", label: "70%" },
  { value: "60", label: "60%" },
  { value: "50", label: "50%" },
  { value: "40", label: "40%" },
  { value: "30", label: "30%" },
  { value: "20", label: "20%" },
  { value: "10", label: "10%" },
];

const numberOfRequiredGuarantorsOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

interface FieldRequestSelectorProps {
  control: Control<BidPostData>;
  fieldRequests: IFieldRequestInput[];
}

export const FieldRequestSelector = ({
  fieldRequests,
  control,
}: FieldRequestSelectorProps) => {
  const {
    formState: { isSubmitting },
  } = useController({
    name: "fieldRequests",
    control,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "fieldRequests",
  });

  const getFieldRequestIndex = useCallback(
    (fieldRequest: IFieldRequestInput) => {
      return fields.findIndex((item) => item.field === fieldRequest.field);
    },
    [fields],
  );

  return (
    <div className="space-y-2">
      <ul className="list-none">
        {fieldRequests.map((fieldRequest) => {
          const fieldIndex = getFieldRequestIndex(fieldRequest);
          const field = fieldIndex > -1 ? fields[fieldIndex] : null;

          return (
            <Fragment
              key={field ? field.id : `field-request-${fieldRequest.field}`}
            >
              <li className="mb-2">
                <Checkbox
                  id={`checkbox-${fieldRequest.field}`}
                  label={
                    FIELD_REQUESTS_LABELS[
                      fieldRequest.field as keyof typeof FIELD_REQUESTS_LABELS
                    ]
                  }
                  checked={!!field}
                  onChange={() => {
                    if (field) {
                      remove(fieldIndex);
                    } else {
                      append(fieldRequest);
                    }
                  }}
                  disabled={isSubmitting}
                />
              </li>

              <AnimatePresence>
                {field && (
                  <motion.div
                    initial={{
                      opacity: 0,
                      height: 0,
                    }}
                    animate={{
                      opacity: 1,
                      height: "auto",
                      transition: {
                        type: "tween",
                        duration: 0.1,
                        ease: "easeOut",
                      },
                    }}
                    exit={{
                      opacity: 0,
                      height: 0,
                      transition: {
                        type: "tween",
                        duration: 0.1,
                        ease: "easeOut",
                      },
                    }}
                  >
                    <DetailsItem className="mb-4 rounded border border-gray-300 p-4">
                      <div className="w-full">
                        <ControlledStringInput
                          label="Comment to customer"
                          name={`fieldRequests.${fieldIndex}.lenderComment`}
                          control={control}
                          required={false}
                        />
                      </div>

                      {field.field === FieldRequestFieldEnum.guarantors &&
                        field.config && (
                          <div className="w-full space-y-4">
                            <FormItem>
                              <Label
                                htmlFor={`number-of-guarantors-${fieldIndex}`}
                                className="text-sm font-bold"
                              >
                                Number of required guarantors*
                              </Label>
                              <Controller
                                name={`fieldRequests.${fieldIndex}.config.numberOfRequiredGuarantors`}
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <Select
                                    value={String(value ?? 1)}
                                    onValueChange={(v) => onChange(Number(v))}
                                    disabled={isSubmitting}
                                  >
                                    <SelectTrigger
                                      id={`number-of-guarantors-${fieldIndex}`}
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {numberOfRequiredGuarantorsOptions.map(
                                        (n) => (
                                          <SelectItem key={n} value={String(n)}>
                                            {String(n)}
                                          </SelectItem>
                                        ),
                                      )}
                                    </SelectContent>
                                  </Select>
                                )}
                              />
                            </FormItem>

                            <FormItem>
                              <Label
                                htmlFor={`guarantors-commitment-${fieldIndex}`}
                                className="text-sm font-bold"
                              >
                                Guarantors commitment
                              </Label>
                              <Controller
                                name={`fieldRequests.${fieldIndex}.config.reducedGuarantorPercentTo`}
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                  <Select
                                    value={
                                      value === null ? "100" : String(value)
                                    }
                                    onValueChange={(v) =>
                                      onChange(v === "100" ? null : Number(v))
                                    }
                                    disabled={isSubmitting}
                                  >
                                    <SelectTrigger
                                      id={`guarantors-commitment-${fieldIndex}`}
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {reducedGuarantorPercentToOptions.map(
                                        (opt) => (
                                          <SelectItem
                                            key={opt.value}
                                            value={opt.value}
                                          >
                                            {opt.label}
                                          </SelectItem>
                                        ),
                                      )}
                                    </SelectContent>
                                  </Select>
                                )}
                              />
                            </FormItem>
                          </div>
                        )}

                      {(field.type !== FieldRequestTypeEnum.COLLATERAL ||
                        field.field === FieldRequestFieldEnum.guarantors) && (
                        <Controller
                          name={`fieldRequests.${fieldIndex}.isCollectedByLender`}
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <Checkbox
                              className="mt-1.5"
                              id={`is_collected_by_lender-${fieldRequest.field}`}
                              label="Information provided directly to lender platform"
                              checked={value}
                              onChange={() => onChange(!value)}
                              disabled={isSubmitting}
                            />
                          )}
                        />
                      )}
                    </DetailsItem>
                  </motion.div>
                )}
              </AnimatePresence>
            </Fragment>
          );
        })}
      </ul>
    </div>
  );
};

export default FieldRequestSelector;
